import { Injectable } from '@angular/core'
import { createPopper, Instance, Options, Modifier, Placement } from '@popperjs/core'

export interface SePositionedObject extends Instance {}
export interface SePositioningServiceOptions extends Options {}
export type SePositioningPlacement = Placement

const OVERFLOW_RX = /scroll|auto/i

@Injectable({providedIn: 'root'})
export class SePositioningService {

  constructor() {
    // noop
  }

  public create(reference: string | Element,
                content: string | Element,
                container?: string | Element,
                options?: SePositioningServiceOptions): SePositionedObject {
    const referenceElement = this.getElement(reference)
    const contentElement = this.getElement(content) as HTMLElement

    if (!options) {
      options = this.defaultOptions
    }

    // Try to find the scroll parent if there is no defined container
    const containerElement = this.findParent(container, contentElement, this.scrollable)

    options.modifiers.forEach(modifier => {
      modifier.options.boundary = containerElement
    })

    return createPopper(referenceElement, contentElement, options)
  }

  private scrollable(el: Element): boolean {
    const style = getComputedStyle(el)
    return OVERFLOW_RX.test(`${style.overflow} ${style.overflowX} ${style.overflowY}`)
  }

  private findParent(container: string | Element, baseEl: Element, matcher: (matchEl: Element) => boolean): Element {
    let el = baseEl
    if (typeof container !== 'string') return container
    while (el = this.shadowParent(el)) {
      if (matcher(el)) return el
    }
  }

  private shadowParent(el: Element): Element {
    return el.parentElement || (el.getRootNode && (el.getRootNode() as any).host)
  }

  public get defaultOptions(): SePositioningServiceOptions {
    return {
      placement: 'top',
      modifiers: [this.flipModifier(), this.preventOverflowModifier(), this.offsetModifier()],
      strategy: 'absolute'
    }
  }

  private flipModifier(): any {
    return {
      name: 'flip',
      enabled: true,
      phase: 'main',
      options: {
        altBoundary: true,
        padding: 12,
        allowedAutoPlacements: ['top', 'bottom', 'left', 'right']
      }
    }
  }

  private preventOverflowModifier(): any {
    return {
      name: 'preventOverflow',
      enabled: true,
      phase: 'main',
      options: {
        altBoundary: true,
        padding: 12
      }
    }
  }

  private offsetModifier(): any {
    return {
      name: 'offset',
      enabled: true,
      phase: 'main',
      options: {
        offset: [0, 4],
      }
    }
  }

  public targetMatchesComponentId(target: HTMLElement, referenceId: string, contentId: string): boolean {
    return !!target.closest(`[id="${referenceId}"],[id="${contentId}"]`)
  }

  private getElement(element: string | Element): Element {
    if (typeof element === 'string') {
      return document.getElementById(element)
    } else {
      return element
    }
  }

}
